import { useEffect, useRef } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Input } from '../ui/input';
import { GOOGLE_MAPS_LIBRARIES, useGoogleMapsRuntimeError } from '../../lib/google-maps';

/**
 * Plain text input that, ONCE the Google Maps + Places library loads,
 * upgrades itself in-place with a native `places.Autocomplete` widget.
 *
 * Critical design rules (learned the hard way):
 *   • The same `<input>` DOM node lives for the lifetime of this component.
 *     We don't conditionally re-render it once Maps loads — that's what
 *     used to grey-out the field and drop keystrokes.
 *   • Typing always works the millisecond the user lands here, even before
 *     Google is loaded (we just don't have suggestions yet).
 *   • If the Places library fails (referrer block, network), the input
 *     stays usable as plain text — no error state visible to the user.
 *   • The Google suggestion dropdown (`.pac-container`) is rendered into
 *     <body> by Google itself; we don't manage it.
 *   • Default location bias is Nigeria — passing `country=null` opts out,
 *     any other ISO-2 code overrides.
 */
// RIVL is Nigeria-first, so unless the caller passes a country we bias
// toward 'ng'. Autocomplete *bias* is set via a LocationBias, not
// componentRestrictions — bias allows worldwide results if the user types a
// city elsewhere, while hard-restricting via componentRestrictions would
// hide them entirely. We default to BIAS mode.
const DEFAULT_BIAS_COUNTRY = 'ng';
// Rough bounding box around Nigeria for Places predictions. These lats/lngs
// cover the whole country with a comfortable margin.
const NIGERIA_BOUNDS = { south: 4.2, west: 2.6, north: 13.9, east: 14.7 };

export function LocationAutocomplete({ value, onChange, onPlace, country, strict = false, ...rest }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey || '', libraries: GOOGLE_MAPS_LIBRARIES });
  const gmErrored = useGoogleMapsRuntimeError(isLoaded);

  const inputRef = useRef(null);
  const acRef = useRef(null);

  // Resolve the ISO-2 country code to use. Caller can override with any
  // explicit value, pass `null` to opt out entirely, or omit for the
  // Nigeria default.
  const biasCountry = country === null ? null : (country || DEFAULT_BIAS_COUNTRY);

  // Attach Google's native Autocomplete to our stable <input> once Maps is ready.
  // Skip attaching entirely if the runtime-error hook detected a billing /
  // referrer failure — otherwise Places will inject its error dialog into
  // document.body on the first keystroke.
  useEffect(() => {
    if (!isLoaded || gmErrored || acRef.current || !inputRef.current) return;
    if (!window.google?.maps?.places?.Autocomplete) return;
    try {
      // In STRICT mode we use componentRestrictions, which hides non-matching
      // results entirely. Default is BIAS mode — set bounds + strictBounds=false
      // so Nigeria sits at the top of suggestions but global places still appear.
      const opts = {
        fields: ['formatted_address', 'name', 'geometry'],
        types: ['geocode', 'establishment'],
      };
      if (biasCountry) {
        if (strict) {
          opts.componentRestrictions = { country: biasCountry };
        } else if (biasCountry === 'ng') {
          opts.bounds = NIGERIA_BOUNDS;
          opts.strictBounds = false;
        } else {
          // Non-NG bias — fall back to componentRestrictions (no bbox provided).
          opts.componentRestrictions = { country: biasCountry };
        }
      }
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, opts);
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place) return;
        const text = place.formatted_address || place.name || '';
        if (text) onChange(text);
        onPlace?.(place);
      });
      acRef.current = ac;
    } catch {
      // Silent — input still works as plain text.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, gmErrored]);

  // Update bias if the caller's country prop changes after attach.
  useEffect(() => {
    if (!acRef.current) return;
    try {
      if (!biasCountry) {
        acRef.current.setComponentRestrictions(null);
      } else if (strict || biasCountry !== 'ng') {
        acRef.current.setComponentRestrictions({ country: biasCountry });
      } else {
        acRef.current.setBounds(NIGERIA_BOUNDS);
      }
    } catch { /* no-op */ }
  }, [biasCountry, strict]);

  // Defensive: never let anything mark the input as disabled/readonly.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (el.disabled) el.disabled = false;
    if (el.readOnly) el.readOnly = false;
  });

  return (
    <Input
      ref={inputRef}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        // Stop the Autocomplete from submitting the parent form when the user
        // hits Enter to accept a suggestion.
        if (e.key === 'Enter') e.preventDefault();
      }}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="words"
      spellCheck="false"
      style={{ opacity: 1, color: 'inherit' }}
      {...rest}
    />
  );
}
